/**
 * Zustand-стор пользователя — единственный владелец его локального
 * состояния: идентификатор, карман, маршрут, уровень, прогресс и билеты.
 *
 * Состояние переживает перезапуск (`persist` в `localStorage`): в
 * десктопном приложении профиль должен открываться на том же месте, где
 * его закрыли. Стор не ходит в сеть — данные о диагностике и уроках
 * приносят экраны и складывают сюда.
 */

import {
  INITIAL_LEVEL,
  type CompleteOnboarding,
  type CurriculumLevel,
  type Strategy,
  type StrategyWeights,
} from '@aptile/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { ProgressEntry } from '../services/forecastService';

/** Распределение до прохождения диагностики — честная монетка. */
const NEUTRAL_WEIGHTS: StrategyWeights = {
  inductiveWeight: 0.5,
  deductiveWeight: 0.5,
};

/** Фраза локального кармана: английский текст — ключ, русский — перевод. */
export interface LocalPhrase {
  readonly en: string;
  readonly ru: string;
}

/** Данные профиля — всё, что стор хранит между сессиями. */
/**
 * Онбординг, пройденный гостем (до входа по почте): аккаунта ещё нет, итог
 * хранится на устройстве и уходит на сервер сразу после входа
 * (`/api/onboarding/complete`). Ответы теста — чтобы сервер проверил их
 * сам; ступень — только для показа до входа.
 */
export interface GuestOnboarding extends CompleteOnboarding {
  readonly level: CurriculumLevel;
  readonly estimatedLevel: number | null;
  readonly finishedAt: string;
}

export interface UserState {
  /** Локальный идентификатор; генерируется при первом запуске. */
  readonly userId: string;
  /**
   * Ключ устройства от сервера: по нему выдаются токены доступа к данным
   * ученика (`api/auth.ts`). Пусто — аккаунт ещё не закреплён.
   */
  readonly deviceKey: string;
  /** Почта аккаунта после входа по коду; `null` — анонимный аккаунт этого устройства. */
  readonly email: string | null;
  /** Как ученик представился Тико (копия с сервера — для приветствий); `null` — не знакомились. */
  readonly userName: string | null;
  /** Активный вокабуляр — «карман» квокки. Целые фразы, не слова. */
  readonly activeVocabulary: readonly LocalPhrase[];
  /** Последний выбранный маршрут; `null`, пока диагностика не пройдена. */
  readonly strategy: Strategy | null;
  /** Текущее распределение стратегий. */
  readonly weights: StrategyWeights;
  /** Текущий уровень владения языком. */
  readonly level: number;
  /** Идентификаторы пройденных уроков — для дашборда и прогресса. */
  readonly completedLessonIds: readonly string[];
  /** Билеты: по одному за каждый завершённый урок. */
  readonly tickets: number;
  /** Журнал пройденных уроков — источник графика и прогноза. */
  readonly progressLog: readonly ProgressEntry[];
  /**
   * Увлечения из онбординга (идентификаторы `HOBBIES`). Задают сцену
   * ассоциативных картинок: предмет рисуется в близком пользователю
   * контексте. Пустой список — генерация без привязки к хобби.
   */
  readonly hobbies: readonly string[];
  /** Опыт за уроки и серии верных ответов — игровая мера усилия. */
  readonly xp: number;
  /**
   * Уровень, подтверждённый игрой-оценкой (шкала 1–6); `null` — игру ещё
   * не проходили. Именно он выбирает набор уроков на карте.
   */
  readonly confirmedLevel: number | null;
  /** Когда пройден онбординг (ISO 8601); `null` — ещё не проходили. */
  readonly onboardedAt: string | null;
  /** Лучшая цепочка в «Словах с квоккой» — число слов ученика. */
  readonly wordGameBest: number;
  /** Итог онбординга гостя, ещё не отправленный на сервер; `null` — нечего отправлять. */
  readonly guestOnboarding: GuestOnboarding | null;
}

/** Переходы состояния. Каждый лишь заменяет поля — без побочных эффектов. */
export interface UserActions {
  /** Заводит `userId`, если профиль ещё не создан. Идемпотентно. */
  ensureUser: () => void;
  /** Аккаунт, закреплённый за устройством сервером. */
  setIdentity: (userId: string, deviceKey: string, email: string | null) => void;
  setUserName: (userName: string | null) => void;
  /** Запоминает маршрут, выбранный диагностикой. */
  setStrategy: (strategy: Strategy) => void;
  /** Стартовая настройка после онбординга: распределение и первая стратегия. */
  calibrate: (weights: StrategyWeights, strategy: Strategy) => void;
  /** Полностью заменяет карман — например, после загрузки с сервера. */
  setVocabulary: (phrases: readonly LocalPhrase[]) => void;
  /**
   * Добавляет фразу в карман, если такой английской фразы ещё нет.
   * Дедупликация — по `en`: повторный проход урока карман не дублирует.
   */
  addPhrase: (en: string, ru: string) => void;
  /**
   * Применяет итог урока: новое распределение, уровень и отметку о
   * прохождении. Билет начисляется только за первый проход урока.
   */
  applyAdaptation: (
    weights: StrategyWeights,
    level: number,
    lessonId: string,
  ) => void;
  /** Дописывает в журнал запись о пройденном уроке (для графика и прогноза). */
  logProgress: (entry: ProgressEntry) => void;
  /** Запоминает увлечения из онбординга — контекст ассоциативных картинок. */
  setHobbies: (hobbies: readonly string[]) => void;
  /** Начисляет опыт (за урок и серии верных ответов). */
  awardXp: (amount: number) => void;
  /** Фиксирует уровень, подтверждённый игрой-оценкой. */
  setAssessedLevel: (level: number) => void;
  /** Отмечает онбординг пройденным: стартовый экран больше его не предлагает. */
  markOnboarded: () => void;
  /** Запоминает результат партии, если он лучше прежнего. */
  recordWordGame: (score: number) => void;
  /** Онбординг гостя пройден: итог ждёт входа; `null` — отправлен на сервер. */
  setGuestOnboarding: (result: GuestOnboarding | null) => void;
  /** Сбрасывает профиль (например, для выхода). */
  reset: () => void;
}

/** Стор целиком: состояние и действия, которые его меняют. */
export type UserStore = UserState & UserActions;

/**
 * Что переживает перезапуск. Подтверждённый уровень сохраняем — иначе
 * после перезагрузки уроки откатились бы к нейтральному набору.
 */
export type PersistedUserState = UserState;

const INITIAL_STATE: UserState = {
  userId: '',
  deviceKey: '',
  email: null,
  userName: null,
  activeVocabulary: [],
  strategy: null,
  weights: NEUTRAL_WEIGHTS,
  level: INITIAL_LEVEL,
  completedLessonIds: [],
  tickets: 0,
  progressLog: [],
  hobbies: [],
  xp: 0,
  confirmedLevel: null,
  onboardedAt: null,
  wordGameBest: 0,
  guestOnboarding: null,
};

/**
 * Вошёл ли ученик: аккаунт с почтой и ключ устройства, по которому сервер
 * выдаёт токены. До входа он гость: онбординг доступен, остальное — нет.
 */
export function isSignedIn(state: Pick<UserState, 'email' | 'deviceKey'>): boolean {
  return state.email !== null && state.deviceKey.length > 0;
}

export const useUserStore = create<UserStore>()(
  persist<UserStore, [], [], PersistedUserState>(
    (set, get) => ({
      ...INITIAL_STATE,

      ensureUser: () => {
        if (get().userId.length > 0) return;
        set({ userId: crypto.randomUUID() });
      },

      setIdentity: (userId, deviceKey, email) => {
        set({ userId, deviceKey, email });
      },

      setUserName: (userName) => {
        if (get().userName !== userName) set({ userName });
      },

      setStrategy: (strategy) => {
        set({ strategy });
      },

      calibrate: (weights, strategy) => {
        set({ weights, strategy });
      },

      setVocabulary: (phrases) => {
        set({ activeVocabulary: phrases });
      },

      addPhrase: (en, ru) => {
        const trimmedEn = en.trim();
        if (trimmedEn.length === 0) return;

        const { activeVocabulary } = get();
        const exists = activeVocabulary.some(
          (candidate) => candidate.en.toLowerCase() === trimmedEn.toLowerCase(),
        );
        if (exists) return;

        set({
          activeVocabulary: [
            ...activeVocabulary,
            { en: trimmedEn, ru: ru.trim() },
          ],
        });
      },

      applyAdaptation: (weights, level, lessonId) => {
        const { completedLessonIds, tickets } = get();
        const firstPass = !completedLessonIds.includes(lessonId);

        set({
          weights,
          level,
          completedLessonIds: firstPass
            ? [...completedLessonIds, lessonId]
            : completedLessonIds,
          tickets: firstPass ? tickets + 1 : tickets,
        });
      },

      setAssessedLevel: (level) => {
        set({ level, confirmedLevel: level });
      },

      logProgress: (entry) => {
        set({ progressLog: [...get().progressLog, entry] });
      },

      setHobbies: (hobbies) => {
        set({ hobbies });
      },

      awardXp: (amount) => {
        if (amount <= 0) return;
        set({ xp: get().xp + amount });
      },

      markOnboarded: () => {
        if (get().onboardedAt === null) set({ onboardedAt: new Date().toISOString() });
      },

      recordWordGame: (score) => {
        if (score > get().wordGameBest) set({ wordGameBest: score });
      },

      setGuestOnboarding: (result) => {
        set({ guestOnboarding: result });
      },

      reset: () => {
        set(INITIAL_STATE);
      },
    }),
    {
      name: 'aptile-user',
      storage: createJSONStorage(() => localStorage),
      // Версия 2: карман хранит фразы (`{en, ru}`), добавлены увлечения и опыт.
      // Версия 3: отметка о пройденном онбординге.
      version: 3,
      migrate: (persisted, version) => {
        const legacy = persisted as Partial<PersistedUserState> & {
          readonly activeVocabulary?: readonly unknown[];
        };
        // Версия 1 — карман перешёл со слов на фразы; v0-строки становятся
        // фразами без перевода, но не теряются. Поля v2 (`hobbies`, `xp`)
        // подставит слияние с начальным состоянием, здесь важен лишь карман.
        const rawVocabulary = version >= 1
          ? (legacy.activeVocabulary ?? []) as readonly LocalPhrase[]
          : (legacy.activeVocabulary ?? [])
              .map((entry) =>
                typeof entry === 'string'
                  ? { en: entry, ru: '' }
                  : (entry as LocalPhrase),
              )
              .filter((phrase) => phrase.en.length > 0);

        // Действий здесь нет — их сольёт сам persist; важен лишь стейт.
        // До версии 3 отметки не было: кто уже выбрал маршрут или прошёл
        // урок, онбординг видел — заново его не показываем.
        const onboardedAt =
          version >= 3
            ? (legacy.onboardedAt ?? null)
            : legacy.strategy != null || (legacy.completedLessonIds?.length ?? 0) > 0
              ? new Date().toISOString()
              : null;

        return { ...legacy, activeVocabulary: rawVocabulary, onboardedAt } as unknown as PersistedUserState;
      },
      partialize: (state): PersistedUserState => ({
        userId: state.userId,
        deviceKey: state.deviceKey,
        email: state.email,
        userName: state.userName,
        activeVocabulary: state.activeVocabulary,
        strategy: state.strategy,
        weights: state.weights,
        level: state.level,
        completedLessonIds: state.completedLessonIds,
        tickets: state.tickets,
        progressLog: state.progressLog,
        hobbies: state.hobbies,
        xp: state.xp,
        confirmedLevel: state.confirmedLevel,
        guestOnboarding: state.guestOnboarding,
        onboardedAt: state.onboardedAt,
        wordGameBest: state.wordGameBest,
      }),
    },
  ),
);
